import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import { calcTableWidth } from '@atlaskit/editor-common/styles';
import { akEditorDefaultLayoutWidth, akEditorGutterPadding, akEditorMediaResizeHandlerPaddingWide, akEditorMobileBreakoutPoint } from '@atlaskit/editor-shared-styles';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { TABLE_MAX_WIDTH } from '../pm-plugins/table-resizing/utils';
import { TableCssClassName as ClassName } from '../types';
import { TableResizer } from './TableResizer';
var getMarginLeft = function getMarginLeft(lineLength, tableWidth) {
  var marginLeft;
  if (tableWidth !== 'inherit' && lineLength) {
    var containerWidth = tableWidth;
    if (containerWidth) {
      marginLeft = (lineLength - containerWidth) / 2;
    }
  }
  return marginLeft;
};
export var InnerContainer = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
    style = _ref.style,
    node = _ref.node,
    children = _ref.children;
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: style,
    className: className,
    "data-number-column": node.attrs.isNumberColumnEnabled,
    "data-layout": node.attrs.layout,
    "data-testid": "table-container"
  }, children);
});
export var ResizableTableContainer = /*#__PURE__*/React.memo(function (_ref2) {
  var _node$attrs$localId, _node$attrs;
  var children = _ref2.children,
    className = _ref2.className,
    node = _ref2.node,
    containerWidth = _ref2.containerWidth,
    editorView = _ref2.editorView,
    getPos = _ref2.getPos,
    tableRef = _ref2.tableRef,
    isResizing = _ref2.isResizing,
    pluginInjectionApi = _ref2.pluginInjectionApi,
    isTableScalingEnabled = _ref2.isTableScalingEnabled,
    tableWrapperHeight = _ref2.tableWrapperHeight,
    isWholeTableInDanger = _ref2.isWholeTableInDanger;
  var containerRef = useRef(null);
  var tableWidthRef = useRef(akEditorDefaultLayoutWidth);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    resizing = _useState2[0],
    setIsResizing = _useState2[1];
  var updateContainerHeight = useCallback(function (height) {
    var _containerRef$current;
    // current StickyHeader State is not stable to be fetch.
    // we need to update stickyHeader plugin to make sure state can be
    //    consistently fetch and refactor below
    var stickyHeaders = (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.getElementsByClassName('pm-table-sticky');
    if (!stickyHeaders || stickyHeaders.length < 1) {
      // when starting to drag, we need to keep the original space,
      // -- When sticky header not appear, margin top(24px) and margin bottom(16px), should be 40px,
      //    1px is border width but collapse make it 0.5.
      // -- When sticky header appear, we should add first row height but reduce
      //    collapsed border
      return typeof height === 'number' ? "".concat(height + 40.5, "px") : 'auto';
    } else {
      var _containerRef$current2;
      var stickyHeaderHeight = ((_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.getElementsByTagName('th')[0].getBoundingClientRect().height) || 0;
      return typeof height === 'number' ? "".concat(height + stickyHeaderHeight + 39.5, "px") : 'auto';
    }
  }, []);
  var onResizeStart = useCallback(function () {
    setIsResizing(true);
  }, []);
  var onResizeStop = useCallback(function () {
    setIsResizing(false);
  }, []);
  var updateWidth = useCallback(function (width) {
    if (!containerRef.current) {
      return;
    }

    // make sure during resizing
    // the pm-table-resizer-container width is the same as its child div resizer-item
    // otherwise when resize table from wider to narrower , pm-table-resizer-container stays wider
    // and cause the fabric-editor-popup-scroll-parent to overflow
    if (containerRef.current.style.width !== "".concat(width, "px")) {
      containerRef.current.style.width = "".concat(width, "px");
    }
  }, []);
  var displayGuideline = useCallback(function (guidelines) {
    var _pluginInjectionApi$g, _pluginInjectionApi$g2;
    return (_pluginInjectionApi$g = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$g2 = pluginInjectionApi.guideline) === null || _pluginInjectionApi$g2 === void 0 || (_pluginInjectionApi$g2 = _pluginInjectionApi$g2.actions) === null || _pluginInjectionApi$g2 === void 0 ? void 0 : _pluginInjectionApi$g2.displayGuideline(editorView)({
      guidelines: guidelines
    })) !== null && _pluginInjectionApi$g !== void 0 ? _pluginInjectionApi$g : false;
  }, [pluginInjectionApi, editorView]);
  var attachAnalyticsEvent = useCallback(function (payload) {
    var _pluginInjectionApi$a;
    return pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$a = pluginInjectionApi.analytics) === null || _pluginInjectionApi$a === void 0 ? void 0 : _pluginInjectionApi$a.actions.attachAnalyticsEvent(payload);
  }, [pluginInjectionApi]);
  var displayGapCursor = useCallback(function (toggle) {
    var _pluginInjectionApi$c, _pluginInjectionApi$c2, _pluginInjectionApi$s;
    return (_pluginInjectionApi$c = pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$c2 = pluginInjectionApi.core) === null || _pluginInjectionApi$c2 === void 0 ? void 0 : _pluginInjectionApi$c2.actions.execute(pluginInjectionApi === null || pluginInjectionApi === void 0 || (_pluginInjectionApi$s = pluginInjectionApi.selection) === null || _pluginInjectionApi$s === void 0 ? void 0 : _pluginInjectionApi$s.commands.displayGapCursor(toggle))) !== null && _pluginInjectionApi$c !== void 0 ? _pluginInjectionApi$c : false;
  }, [pluginInjectionApi]);
  var tableWidth = getTableContainerWidth(node);
  var _useSharedPluginState = useSharedPluginState(pluginInjectionApi, ['table']),
    tableState = _useSharedPluginState.tableState;
  var _ref3 = tableState,
    widthToWidest = _ref3.widthToWidest;
  var currentTableNodeLocalId = (_node$attrs$localId = node === null || node === void 0 || (_node$attrs = node.attrs) === null || _node$attrs === void 0 ? void 0 : _node$attrs.localId) !== null && _node$attrs$localId !== void 0 ? _node$attrs$localId : '';

  // 76 is currently an accepted padding value considering the spacing for resizer handle
  var responsiveContainerWidth = isTableScalingEnabled ? containerWidth - akEditorGutterPadding * 2 - akEditorMediaResizeHandlerPaddingWide / 2 : containerWidth - akEditorGutterPadding * 2 - akEditorMediaResizeHandlerPaddingWide;
  var width = Math.min(tableWidth, responsiveContainerWidth);
  if (isTableScalingEnabled && currentTableNodeLocalId && widthToWidest && widthToWidest[currentTableNodeLocalId]) {
    width = TABLE_MAX_WIDTH;
  }
  if (!isResizing) {
    tableWidthRef.current = width;
  }
  var maxResizerWidth = Math.min(responsiveContainerWidth, TABLE_MAX_WIDTH);
  var tableResizerProps = {
    width: width,
    maxWidth: maxResizerWidth,
    containerWidth: containerWidth,
    updateWidth: updateWidth,
    editorView: editorView,
    getPos: getPos,
    node: node,
    tableRef: tableRef,
    displayGuideline: displayGuideline,
    attachAnalyticsEvent: attachAnalyticsEvent,
    displayGapCursor: displayGapCursor,
    isTableScalingEnabled: isTableScalingEnabled,
    isWholeTableInDanger: isWholeTableInDanger,
    pluginInjectionApi: pluginInjectionApi
  };
  if (getBooleanFF('platform.editor.resizing-table-height-improvement')) {
    tableResizerProps = _objectSpread(_objectSpread({}, tableResizerProps), {}, {
      onResizeStart: onResizeStart,
      onResizeStop: onResizeStop
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: tableWidthRef.current,
      height: resizing ? updateContainerHeight(tableWrapperHeight !== null && tableWrapperHeight !== void 0 ? tableWrapperHeight : 'auto') : 'auto'
    },
    className: ClassName.TABLE_RESIZER_CONTAINER,
    ref: containerRef
  }, /*#__PURE__*/React.createElement(TableResizer, tableResizerProps, /*#__PURE__*/React.createElement(InnerContainer, {
    className: className,
    node: node
  }, children))));
});
export var TableContainer = function TableContainer(_ref4) {
  var children = _ref4.children,
    node = _ref4.node,
    className = _ref4.className,
    _ref4$containerWidth = _ref4.containerWidth,
    lineLength = _ref4$containerWidth.lineLength,
    editorWidth = _ref4$containerWidth.width,
    isTableResizingEnabled = _ref4.isTableResizingEnabled,
    isBreakoutEnabled = _ref4.isBreakoutEnabled,
    editorView = _ref4.editorView,
    getPos = _ref4.getPos,
    tableRef = _ref4.tableRef,
    isNested = _ref4.isNested,
    tableWrapperHeight = _ref4.tableWrapperHeight,
    isResizing = _ref4.isResizing,
    pluginInjectionApi = _ref4.pluginInjectionApi,
    isTableScalingEnabled = _ref4.isTableScalingEnabled,
    isWholeTableInDanger = _ref4.isWholeTableInDanger;
  if (isTableResizingEnabled && !isNested) {
    return /*#__PURE__*/React.createElement(ResizableTableContainer, {
      className: className,
      node: node,
      containerWidth: editorWidth,
      editorView: editorView,
      getPos: getPos,
      tableRef: tableRef,
      tableWrapperHeight: tableWrapperHeight,
      isResizing: isResizing,
      pluginInjectionApi: pluginInjectionApi,
      isTableScalingEnabled: isTableScalingEnabled,
      isWholeTableInDanger: isWholeTableInDanger
    }, children);
  }
  var tableWidth = isBreakoutEnabled ? calcTableWidth(node.attrs.layout, editorWidth) : 'inherit';
  return /*#__PURE__*/React.createElement(InnerContainer, {
    node: node,
    className: classNames(className, {
      'less-padding': editorWidth < akEditorMobileBreakoutPoint && !isNested
    }),
    style: {
      width: tableWidth,
      marginLeft: getMarginLeft(lineLength, tableWidth)
    }
  }, children);
};